import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Wifi, Eye, EyeOff, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local email/password login is only exposed when the build explicitly sets
// REACT_APP_ENABLE_LOCAL_LOGIN to the string "true" AND the build does not
// set REACT_APP_DISABLE_LOCAL_LOGIN to "true" (hard kill-switch for
// production). When the flag is missing or any other value the login page
// shows only the Microsoft SSO button — that is the safe default for
// production.
//
// CRA loads env files in this priority order:
//   yarn start → .env.development.local > .env.local > .env.development > .env
//   yarn build → .env.production.local > .env.local > .env.production > .env
// So keeping the enable flag only in .env.development and an explicit
// `REACT_APP_DISABLE_LOCAL_LOGIN=true` in .env.production gives us a
// double-guard that cannot be turned back on by accident during a prod build.
const LOCAL_LOGIN_ENABLED =
  process.env.REACT_APP_ENABLE_LOCAL_LOGIN === "true" &&
  process.env.REACT_APP_DISABLE_LOCAL_LOGIN !== "true";

// Microsoft Logo SVG Component
const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [microsoftConfigured, setMicrosoftConfigured] = useState(false);
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if Microsoft auth is configured
    const checkMicrosoftStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/microsoft/status`);
        setMicrosoftConfigured(response.data.configured);
      } catch (error) {
        console.log("Microsoft auth not available");
      }
    };
    checkMicrosoftStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Por favor, completa todos los campos");
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success("Bienvenido a WiLAN");
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/microsoft/login`);
      // Redirect to Microsoft login page
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Microsoft login error:", error);
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Error al iniciar sesión con Microsoft: ${detail}` : "Error al iniciar sesión con Microsoft");
      setMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#04042C] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwYWJzdHJhY3QlMjBkYXJrJTIwYmx1ZSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxNjgzMjc1fDA&ixlib=rb-4.1.0&q=85')`
        }}
      ></div>

      {/* Gradient Orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-[#42E695] rounded-full blur-[128px] opacity-20"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#03BEE4] rounded-full blur-[128px] opacity-15"></div>

      <Card className="w-full max-w-md bg-[#0A0A35]/90 backdrop-blur-lg border-[#1E1E5F] shadow-2xl relative z-10">
        <CardHeader className="space-y-4 text-center pb-2">
          {/* Logo */}
          <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center">
            <img 
              src="/wilan-logo.png" 
              alt={settings.platform_name}
              className="w-full h-full object-contain"
            />
          </div>
          
          <div>
            <CardTitle className="text-2xl font-heading font-semibold text-white">
              {settings.platform_name}
            </CardTitle>
            <CardDescription className="text-slate-400 mt-2">
              {settings.platform_subtitle || "Plataforma Central de Operaciones"}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          {/* Microsoft Login Button */}
          <Button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={microsoftLoading || !microsoftConfigured}
            data-testid="microsoft-login-btn"
            className="w-full bg-white text-gray-700 hover:bg-gray-100 font-medium h-11 border border-gray-300 mb-4 flex items-center justify-center gap-3"
          >
            {microsoftLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Conectando con Microsoft...
              </>
            ) : (
              <>
                <MicrosoftLogo />
                Iniciar sesión con Microsoft
              </>
            )}
          </Button>

          <div className="relative my-6" style={{ display: LOCAL_LOGIN_ENABLED ? undefined : "none" }}>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#1E1E5F]"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#0A0A35] px-4 text-slate-400">
                o con email y contraseña
              </span>
            </div>
          </div>

          {LOCAL_LOGIN_ENABLED && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="local-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="bg-[#0A0A35] border-[#1E1E5F] text-white placeholder:text-slate-600 focus:border-[#03BEE4] focus:ring-1 focus:ring-[#03BEE4]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="bg-[#0A0A35] border-[#1E1E5F] text-white placeholder:text-slate-600 focus:border-[#03BEE4] focus:ring-1 focus:ring-[#03BEE4] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#42E695] text-[#04042C] hover:bg-[#34D399] font-semibold h-11 shadow-glow-green transition-all duration-200"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </form>
          )}

          {/* End of form */}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
